import React, { useState, useEffect } from 'react';
import { X, Download, Send, AlertCircle, FileText, FileEdit, Copy, CheckCircle } from 'lucide-react';
import { authFetch, getLocalApiUrl } from '../config/api';
import { formatSmrReportDateTime } from '../utils/smrDateDisplay';

const SmrPreviewModal = ({ caseId, isOpen, onClose }) => {
  const [smrData, setSmrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState('form'); // 'form' or 'verbal'
  const [copied, setCopied] = useState(false);
  const [previousSmrs, setPreviousSmrs] = useState([]);
  const [evidence, setEvidence] = useState(null);

  useEffect(() => {
    if (isOpen && caseId) {
      loadSmrData();
    }
  }, [isOpen, caseId]);

  useEffect(() => {
    if (isOpen && caseId) {
      (async () => {
        try {
          const [smrRes, evRes] = await Promise.all([
            authFetch(getLocalApiUrl(`/api/cases/${caseId}/previous-smrs`)),
            authFetch(getLocalApiUrl(`/api/cases/${caseId}/evidence?limit=100`))
          ]);
          const smrJ = await smrRes.json();
          const evJ = await evRes.json();
          if (smrJ.success) setPreviousSmrs(smrJ.data || []);
          if (evJ.success && evJ.data?.items) setEvidence(evJ.data);
        } catch (_) {}
      })();
    }
  }, [isOpen, caseId]);

  const loadSmrData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Add regenerate=true to always get fresh data with latest mock profiles
      const response = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/forms/smr?regenerate=true`));
      const result = await response.json();
      if (result.success) {
        setSmrData(result.data);
      } else {
        setError(result.error || 'Failed to load SMR data');
      }
    } catch (e) {
      console.error('Error loading SMR data:', e);
      setError('Failed to load SMR data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/smr/download`));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SMR_${smrData?.partH?.internalReference || caseId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error downloading SMR:', e);
      alert('Failed to download SMR PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-psg-teal/10 flex items-center justify-center">
              <FileText className="h-5 w-5 text-psg-teal" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Suspicious Matter Report (SMR)</h2>
              <p className="text-sm text-gray-500">AUSTRAC Compliance Report Preview</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        {!loading && !error && smrData && (
          <div className="px-6 py-2 border-b border-gray-200 flex gap-2 bg-gray-50">
            <button
              onClick={() => setActiveTab('form')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                activeTab === 'form' 
                  ? 'bg-white text-psg-teal shadow-sm border border-gray-200' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <FileText className="h-4 w-4 inline mr-2" />
              Form View
            </button>
            <button
              onClick={() => setActiveTab('verbal')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition ${
                activeTab === 'verbal' 
                  ? 'bg-white text-psg-teal shadow-sm border border-gray-200' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <FileEdit className="h-4 w-4 inline mr-2" />
              Verbal Report Narrative
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-psg-teal"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Error Loading SMR</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && smrData && activeTab === 'verbal' && (
            <VerbalSmrScript smrData={smrData} onCopy={() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }} copied={copied} />
          )}

          {!loading && !error && smrData && activeTab === 'form' && (
            <div className="space-y-6">
              {/* Report Header */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold text-gray-700">Internal Reference:</span>
                    <span className="ml-2 text-gray-900">{smrData.partH?.internalReference}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Report Date:</span>
                    <span className="ml-2 text-gray-900">
                      {formatSmrReportDateTime(smrData.partH?.reportDate, smrData.partH?.reportDateDisplay)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Previous SMRs (same member) */}
              {previousSmrs.length > 0 && (
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <h4 className="font-semibold text-amber-900 mb-2">Previous SMRs (Same Member)</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-amber-300 text-amber-800">
                          <th className="text-left py-1">Case #</th>
                          <th className="text-left py-1">Status</th>
                          <th className="text-left py-1">Created</th>
                          <th className="text-left py-1">AUSTRAC Ref</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previousSmrs.map(r => (
                          <tr key={r.id} className="border-b border-amber-200">
                            <td className="py-1 font-mono">{r.case_number || r.id}</td>
                            <td className="py-1">{r.status || '—'}</td>
                            <td className="py-1">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                            <td className="py-1">{r.regulatory_ref || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Selected Evidence / Transactions */}
              {evidence?.items?.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Selected Evidence ({evidence.items.length} items)</h4>
                  <div className="overflow-x-auto max-h-48 overflow-y-auto">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 bg-blue-100">
                        <tr>
                          <th className="text-left py-1">Time</th>
                          <th className="text-left py-1">Type</th>
                          <th className="text-left py-1">Venue</th>
                          <th className="text-left py-1">EGM</th>
                          <th className="text-left py-1">CashIn</th>
                          <th className="text-left py-1">CashOut</th>
                          <th className="text-left py-1">Turnover</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evidence.items.map((it, idx) => {
                          const ts = it.timestamp || it.TransactionTime || it.SnapshotTime || it.StartTime;
                          const isSession = !!(it.SessionPK || it.StartTime);
                          return (
                            <tr key={it.TransactionPK || it.SessionPK || idx} className="border-b border-blue-200">
                              <td className="py-1">{ts ? new Date(ts).toLocaleString() : '—'}</td>
                              <td className="py-1">{isSession ? 'Session' : 'Transaction'}</td>
                              <td className="py-1">{it.Venue || it.venue || '—'}</td>
                              <td className="py-1">{it.EgmHouseNo || it.egm_house_no || '—'}</td>
                              <td className="py-1">{it.CashIn ?? it.cash_in ?? '—'}</td>
                              <td className="py-1">{it.CashOut ?? it.cash_out ?? '—'}</td>
                              <td className="py-1">{it.Turnover ?? it.turnover ?? '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Transaction Date Range Validation */}
              {smrData?.meta?.dateRangeValidation && (
                <div className={`rounded-lg p-4 border-2 ${
                  smrData.meta.dateRangeValidation.valid 
                    ? 'bg-green-50 border-green-300' 
                    : 'bg-red-50 border-red-400'
                }`}>
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 ${smrData.meta.dateRangeValidation.valid ? 'text-green-600' : 'text-red-600'}`}>
                      {smrData.meta.dateRangeValidation.valid ? (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className={`font-semibold text-base mb-2 ${
                        smrData.meta.dateRangeValidation.valid ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {smrData.meta.dateRangeValidation.valid ? '✓ Transaction Date Range Valid' : '⚠️ Transaction Date Range INVALID'}
                      </div>
                      <div className={`text-sm mb-3 ${
                        smrData.meta.dateRangeValidation.valid ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {smrData.meta.dateRangeValidation.message}
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className={smrData.meta.dateRangeValidation.valid ? 'text-green-700' : 'text-red-700'}>
                          <div className="font-semibold">First Transaction:</div>
                          <div>{smrData.meta.dateRangeValidation.first_date || '-'}</div>
                        </div>
                        <div className={smrData.meta.dateRangeValidation.valid ? 'text-green-700' : 'text-red-700'}>
                          <div className="font-semibold">Last Transaction:</div>
                          <div>{smrData.meta.dateRangeValidation.last_date || '-'}</div>
                        </div>
                        <div className={smrData.meta.dateRangeValidation.valid ? 'text-green-700' : 'text-red-700'}>
                          <div className="font-semibold">Time Span:</div>
                          <div>{smrData.meta.dateRangeValidation.span_days} days ({smrData.meta.dateRangeValidation.span_hours} hours)</div>
                        </div>
                        <div className={smrData.meta.dateRangeValidation.valid ? 'text-green-700' : 'text-red-700'}>
                          <div className="font-semibold">AUSTRAC Requirement:</div>
                          <div>≤ 3 days</div>
                        </div>
                      </div>
                      {!smrData.meta.dateRangeValidation.valid && (
                        <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded text-sm text-red-900">
                          <div className="font-semibold mb-1">⚠️ SMR VALIDATION FAILED</div>
                          <div>AUSTRAC requires that all transactions in an SMR must fall within a 3-day period. This report currently spans {smrData.meta.dateRangeValidation.span_days} days. Please review and exclude transactions that fall outside the appropriate 3-day window before submission.</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Part A: Designated Services */}
              <Section title="Part A: Designated Services">
                <div className="flex flex-wrap gap-2">
                  {smrData.partA?.designatedSvc?.map((service, idx) => (
                    <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                      {service}
                    </span>
                  ))}
                  {(!smrData.partA?.designatedSvc || smrData.partA.designatedSvc.length === 0) && (
                    <span className="text-gray-500 text-sm">No services specified</span>
                  )}
                </div>
              </Section>

              {/* Part B: Grounds for Suspicion */}
              <Section title="Part B: Grounds for Suspicion">
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">Suspicion Reasons:</h4>
                    <div className="flex flex-wrap gap-2">
                      {smrData.partB?.suspReason?.map((reason, idx) => (
                        <span key={idx} className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm text-gray-700 mb-1">Grounds for Suspicion:</h4>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border border-gray-200">
                      {smrData.partB?.groundsForSuspicion || '-'}
                    </p>
                  </div>
                </div>
              </Section>

              {/* Part C: Subject Details */}
              <Section title="Part C: Subject Details (Member/Patron)">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <InfoRow label="Full Name" value={smrData.partC?.primary?.fullName} />
                  <InfoRow label="Date of Birth" value={smrData.partC?.primary?.individualDOB} />
                  <InfoRow label="Customer ID" value={smrData.partC?.primary?.customerId} />
                  <InfoRow label="Phone" value={smrData.partC?.primary?.phone} />
                  <InfoRow label="Email" value={smrData.partC?.primary?.email} />
                  <InfoRow label="Citizenship" value={smrData.partC?.primary?.individualCitizenCountry} />
                  {smrData.partC?.primary?.memberSince && (
                    <InfoRow label="Member Since" value={smrData.partC.primary.memberSince} />
                  )}
                  {smrData.partC?.primary?.verificationStatus && (
                    <InfoRow label="Verification Status" value={smrData.partC.primary.verificationStatus} />
                  )}
                </div>
                
                {smrData.partC?.primary?.residentialAddress && (
                  <div className="mt-3">
                    <h4 className="font-semibold text-sm text-gray-700 mb-1">Residential Address:</h4>
                    <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border border-gray-200">
                      {smrData.partC.primary.residentialAddress.line1}
                      {smrData.partC.primary.residentialAddress.line2 && `, ${smrData.partC.primary.residentialAddress.line2}`}
                      <br />
                      {smrData.partC.primary.residentialAddress.suburb}, {smrData.partC.primary.residentialAddress.state} {smrData.partC.primary.residentialAddress.postcode}
                      <br />
                      {smrData.partC.primary.residentialAddress.country}
                    </p>
                  </div>
                )}
                
                {/* KYC Documents */}
                {smrData.partC?.kycDocuments && smrData.partC.kycDocuments.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">KYC Documents:</h4>
                    <div className="space-y-2">
                      {smrData.partC.kycDocuments.map((doc, idx) => (
                        <div key={idx} className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                          <div className="grid grid-cols-2 gap-2">
                            <div><span className="font-semibold">Type:</span> {doc.documentType}</div>
                            <div><span className="font-semibold">Number:</span> {doc.documentNumber}</div>
                            <div><span className="font-semibold">Verified:</span> {doc.verificationDate}</div>
                            <div><span className="font-semibold">By:</span> {doc.verifiedBy}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Section>
              
              {/* Part D: Venue Information */}
              {smrData.partD?.venues && smrData.partD.venues.length > 0 && (
                <Section title="Part D: Venue Information">
                  <div className="space-y-3">
                    {smrData.partD.venues.map((venue, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-200 rounded p-3">
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="col-span-2">
                            <span className="font-semibold text-gray-900">{venue.name}</span>
                            {venue.code && <span className="ml-2 text-gray-600">({venue.code})</span>}
                          </div>
                          <div className="col-span-2"><span className="font-semibold">Address:</span> {venue.address}</div>
                          {venue.licenseNumber && <div className="col-span-2"><span className="font-semibold">License:</span> {venue.licenseNumber}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Part F: Transaction Evidence */}
              <Section title="Part F: Transaction Evidence">
                {smrData.partF?.transactions && smrData.partF.transactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Date/Time</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Venue</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Device</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {smrData.partF.transactions.slice(0, 10).map((txn, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">{txn.date || '-'}</td>
                            <td className="px-3 py-2">{txn.venue || '-'}</td>
                            <td className="px-3 py-2">{txn.device || '-'}</td>
                            <td className="px-3 py-2 text-right font-mono">{txn.amount || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {smrData.partF.transactions.length > 10 && (
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Showing 10 of {smrData.partF.transactions.length} transactions (full list in PDF)
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No transaction evidence available</p>
                )}
              </Section>

              {/* Part G: Offence Type */}
              <Section title="Part G: Offence Type & Previous Reports">
                <div className="space-y-2">
                  <InfoRow label="Offence Type" value={smrData.partG?.offenceType || 'Not specified'} />
                  <InfoRow label="Previous Reports" value={smrData.partG?.previousReports?.length || 0} />
                </div>
              </Section>

              {/* Part H: Reporting Entity */}
              <Section title="Part H: Reporting Entity Information">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <InfoRow label="Entity Name" value={smrData.partH?.reportingEntity?.name} />
                  <InfoRow label="Venue(s)" value={smrData.partH?.reportingEntity?.venues?.join(', ')} />
                  <InfoRow label="Contact Person" value={smrData.partH?.contact?.name} />
                  <InfoRow label="Contact Email" value={smrData.partH?.contact?.email} />
                </div>
              </Section>

              {/* Compliance Notice */}
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-semibold text-amber-900 mb-1">AUSTRAC Submission Requirements</p>
                    <p className="text-amber-800">
                      • Terrorism Financing: Submit within <strong>24 hours</strong> of forming suspicion
                      <br />
                      • Money Laundering/Other: Submit within <strong>3 business days</strong>
                      <br />
                      • Ensure all six essentials are addressed: Who, What, Where, When, Why, How
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {!loading && !error && smrData && (
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-600">
              <span className="font-semibold">Status:</span> Draft (Not Submitted)
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} className="btn-secondary">
                Close
              </button>
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {downloading ? 'Downloading...' : 'Download PDF'}
              </button>
              <button
                className="px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed flex items-center gap-2"
                disabled
                title="AUSTRAC submission integration coming soon"
              >
                <Send className="h-4 w-4" />
                Submit to AUSTRAC
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components
const Section = ({ title, children }) => (
  <div className="border border-gray-200 rounded-lg overflow-hidden">
    <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
      <h3 className="font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="p-4">
      {children}
    </div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div>
    <span className="font-semibold text-gray-700">{label}:</span>
    <span className="ml-2 text-gray-900">{value || '-'}</span>
  </div>
);

// Verbal SMR Narrative Generator Component
const VerbalSmrScript = ({ smrData, onCopy, copied }) => {
  const partC = smrData.partC?.primary || {};
  const partB = smrData.partB || {};
  const partG = smrData.partG || {};
  const partH = smrData.partH || {};
  const partF = smrData.partF || {};
  const meta = smrData.meta || {};
  const partD = smrData.partD || {};
  
  // Helper to format currency
  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    const num = parseFloat(String(amount).replace(/[^0-9.-]/g, ''));
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(num);
  };

  // Calculate totals
  const txnCount = partF.transactions?.length || 0;
  const totalAmount = partF.transactions?.reduce((sum, t) => {
    const amt = parseFloat(String(t.amount || '0').replace(/[^0-9.-]/g, ''));
    return sum + (isNaN(amt) ? 0 : amt);
  }, 0) || 0;

  // Format address
  const formatAddress = (addr) => {
    if (!addr) return 'address not on file';
    return `${addr.line1 || ''}, ${addr.suburb || ''}, ${addr.state || ''} ${addr.postcode || ''}`.replace(/^,\s*/, '').replace(/,\s*,/g, ',');
  };

  // Get dates
  const firstDate = meta.dateRangeValidation?.first_date || 'unknown date';
  const lastDate = meta.dateRangeValidation?.last_date || 'unknown date';
  const spanDays = meta.dateRangeValidation?.span_days || 0;

  // Build transaction narrative
  const buildTransactionNarrative = () => {
    if (!partF.transactions || partF.transactions.length === 0) {
      return 'No specific transactions have been recorded for this suspicious matter.';
    }

    // Create venue lookup from partD for better names
    const venueLookup = {};
    (partD.venues || []).forEach(v => {
      if (v.code) venueLookup[v.code] = v.name;
      if (v.name) venueLookup[v.name] = v.name;
    });

    const primaryVenueName = partD.venues?.[0]?.name || partH.reportingEntity?.venues?.[0] || partH.reportingEntity?.name || 'the venue';

    const txns = partF.transactions.slice(0, 10);
    let narrative = txns.map((t, idx) => {
      const date = t.date || 'unknown date';
      const amount = t.amount || 'unknown amount';
      // Look up venue name - try lookup first, then fallback to transaction venue, then primary venue
      const venue = venueLookup[t.venue] || t.venue || primaryVenueName;
      const device = t.device ? ` on device ${t.device}` : '';
      return `On ${date}, the subject conducted a transaction of ${amount} at ${venue}${device}.`;
    }).join(' ');

    if (partF.transactions.length > 10) {
      narrative += ` Additionally, there were ${partF.transactions.length - 10} more transactions during this period that follow a similar pattern.`;
    }

    return narrative;
  };

  // Generate the narrative sections
  const sections = {
    overview: `This Suspicious Matter Report concerns ${partC.fullName || 'the subject'} (Member ID: ${partC.customerId || 'unknown'}), a ${partC.memberSince ? `member since ${partC.memberSince}` : 'patron'} of ${partH.reportingEntity?.name || 'this reporting entity'}. The suspicious activity was identified through our transaction monitoring systems and relates to ${smrData.partA?.designatedSvc?.join(', ') || 'gaming machine services'}.`,

    subject: `The subject of this report is ${partC.fullName || '[Name unknown]'}, born ${partC.individualDOB || '[DOB unknown]'}. The subject's registered address is ${formatAddress(partC.residentialAddress)}. Contact details on file include phone number ${partC.phone || 'not recorded'} and email ${partC.email || 'not recorded'}. The subject holds ${partC.individualCitizenCountry || 'Australian'} citizenship and has been verified through ${smrData.partC?.kycDocuments?.length > 0 ? smrData.partC.kycDocuments.map(d => d.documentType).join(', ') : 'standard identification documents'}.`,

    activity: `The suspicious activity occurred between ${firstDate} and ${lastDate}, spanning approximately ${spanDays} days. During this period, the subject conducted ${txnCount} transaction${txnCount !== 1 ? 's' : ''} totalling ${formatCurrency(totalAmount)}. ${buildTransactionNarrative()}`,

    location: (() => {
      const primaryVenue = partD.venues?.[0];
      const venueName = primaryVenue?.name || partH.reportingEntity?.venues?.[0] || partH.reportingEntity?.name || 'our venue';
      const venueCode = primaryVenue?.code ? ` (Venue Code: ${primaryVenue.code})` : '';
      const venueAddress = primaryVenue?.address || partH.reportingEntity?.address || '';
      const additionalVenues = partD.venues?.length > 1 
        ? ` The subject was also observed at ${partD.venues.slice(1).map(v => `${v.name}${v.address ? ` (${v.address})` : ''}`).join('; ')}.`
        : '';
      return `The activity took place at ${venueName}${venueCode}${venueAddress ? `, located at ${venueAddress}` : ''}.${additionalVenues}`;
    })(),

    suspicion: `Our grounds for suspicion are as follows: ${partB.groundsForSuspicion || 'The transaction patterns observed are inconsistent with the subject\'s known profile and legitimate business activities.'} The specific indicators that raised our concern include ${partB.suspReason?.join(', ') || 'unusual transaction patterns'}. ${partB.nature ? `The nature of this suspicious activity is: ${partB.nature}.` : ''} ${partB.suspReasonOther ? `Additional observations: ${partB.suspReasonOther}` : ''}`,

    assessment: `Based on our assessment, the suspicious activity may be linked to ${partG.offenceType || partG.offenceDescription || 'money laundering or proceeds of crime'}. The patterns observed, including the volume, frequency, and nature of transactions, are inconsistent with legitimate gaming activity and suggest ${partG.offenceDescription || 'potential criminal activity'}. ${partG.previousReports?.length > 0 ? `This subject has been the subject of ${partG.previousReports.length} previous report(s) to AUSTRAC.` : 'This is the first report regarding this subject.'}`,

    entity: `This report is submitted by ${partH.reportingEntity?.name || '[Entity Name]'} in accordance with our obligations under the Anti-Money Laundering and Counter-Terrorism Financing Act 2006. The contact person for this matter is ${partH.contact?.name || '[Contact Name]'}, ${partH.contact?.position || 'Compliance Officer'}, who can be reached at ${partH.contact?.email || '[email]'} or ${partH.contact?.phone || '[phone]'}. Our internal reference for this matter is ${partH.internalReference || smrData.caseId || '[Reference]'}.`
  };

  // Generate plain text version for copying
  const generatePlainText = () => {
    return `SUSPICIOUS MATTER REPORT - NARRATIVE STATEMENT

Internal Reference: ${partH.internalReference || smrData.caseId || 'TBA'}
Report Date: ${new Date().toLocaleDateString('en-AU', { dateStyle: 'long', timeZone: 'Australia/Sydney' })}

OVERVIEW
${sections.overview}

SUBJECT IDENTIFICATION (WHO)
${sections.subject}

SUSPICIOUS ACTIVITY (WHAT & WHEN)
${sections.activity}

LOCATION (WHERE)
${sections.location}

GROUNDS FOR SUSPICION (WHY)
${sections.suspicion}

OFFENCE ASSESSMENT (HOW)
${sections.assessment}

REPORTING ENTITY
${sections.entity}

---
Report generated by PaySafeGO Compliance System
`;
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatePlainText());
    onCopy();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-lg p-5 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Verbal SMR Narrative</h3>
            <p className="text-slate-300 text-sm mt-1">Formatted narrative for AUSTRAC online submission</p>
            <p className="text-slate-400 text-xs mt-2">Internal Ref: {partH.internalReference || smrData.caseId || 'TBA'}</p>
          </div>
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 px-5 py-2.5 bg-white text-slate-800 font-semibold rounded-lg hover:bg-slate-100 transition shadow-lg"
          >
            {copied ? <CheckCircle className="h-5 w-5 text-green-600" /> : <Copy className="h-5 w-5" />}
            {copied ? 'Copied!' : 'Copy Full Report'}
          </button>
        </div>
      </div>

      {/* Validation Status */}
      {meta.dateRangeValidation && (
        <div className={`rounded-lg p-4 flex items-center gap-3 ${
          meta.dateRangeValidation.valid 
            ? 'bg-green-50 border border-green-200 text-green-800' 
            : 'bg-red-50 border border-red-200 text-red-800'
        }`}>
          {meta.dateRangeValidation.valid ? (
            <CheckCircle className="h-6 w-6 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-6 w-6 flex-shrink-0" />
          )}
          <div>
            <div className="font-semibold">
              {meta.dateRangeValidation.valid 
                ? '✓ Transaction Period Valid (Within 3-Day Requirement)'
                : '⚠️ Transaction Period Exceeds 3 Days - Review Required'
              }
            </div>
            <div className="text-sm opacity-80">
              {firstDate} to {lastDate} ({spanDays} days)
            </div>
          </div>
        </div>
      )}

      {/* Narrative Sections */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Overview */}
        <NarrativeSection 
          title="Overview" 
          icon="📋"
          content={sections.overview}
          bgClass="bg-slate-50"
        />

        {/* Subject */}
        <NarrativeSection 
          title="Subject Identification" 
          subtitle="WHO is this report about?"
          icon="👤"
          content={sections.subject}
        />

        {/* Activity */}
        <NarrativeSection 
          title="Suspicious Activity" 
          subtitle="WHAT happened & WHEN?"
          icon="⚠️"
          content={sections.activity}
          bgClass="bg-amber-50/50"
        />

        {/* Location */}
        <NarrativeSection 
          title="Location Details" 
          subtitle="WHERE did this occur?"
          icon="📍"
          content={sections.location}
        />

        {/* Suspicion */}
        <NarrativeSection 
          title="Grounds for Suspicion" 
          subtitle="WHY is this suspicious?"
          icon="🔍"
          content={sections.suspicion}
          bgClass="bg-red-50/50"
        />

        {/* Assessment */}
        <NarrativeSection 
          title="Offence Assessment" 
          subtitle="HOW was this conducted?"
          icon="⚖️"
          content={sections.assessment}
        />

        {/* Entity */}
        <NarrativeSection 
          title="Reporting Entity" 
          icon="🏢"
          content={sections.entity}
          bgClass="bg-slate-50"
          isLast
        />
      </div>

      {/* Transaction Summary Table */}
      {partF.transactions && partF.transactions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
            <h4 className="font-semibold text-gray-900">Transaction Evidence Summary</h4>
            <p className="text-sm text-gray-600">{txnCount} transactions totalling {formatCurrency(totalAmount)}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Date/Time</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Venue</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Device</th>
                  <th className="px-4 py-3 text-right font-medium text-gray-600">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {partF.transactions.slice(0, 8).map((txn, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-gray-900">{txn.date || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-700">{txn.venue || '-'}</td>
                    <td className="px-4 py-2.5 text-gray-700">{txn.device || '-'}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-gray-900">{txn.amount || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {partF.transactions.length > 8 && (
            <div className="bg-gray-50 px-4 py-2 text-center text-sm text-gray-500 border-t">
              + {partF.transactions.length - 8} more transactions (see full report)
            </div>
          )}
        </div>
      )}

      {/* Submission Info */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h4 className="font-semibold text-blue-900 text-sm mb-1">📞 AUSTRAC Contact</h4>
          <p className="text-lg font-bold text-blue-700">1300 021 037</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
          <h4 className="font-semibold text-amber-900 text-sm mb-1">⏰ ML/Other Deadline</h4>
          <p className="text-lg font-bold text-amber-700">3 Business Days</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <h4 className="font-semibold text-red-900 text-sm mb-1">🚨 TF Deadline</h4>
          <p className="text-lg font-bold text-red-700">24 Hours</p>
        </div>
      </div>
    </div>
  );
};

// Narrative Section Component
const NarrativeSection = ({ title, subtitle, icon, content, bgClass = '', isLast = false }) => (
  <div className={`${bgClass} ${!isLast ? 'border-b border-gray-200' : ''}`}>
    <div className="px-5 py-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{icon}</span>
        <div>
          <h4 className="font-semibold text-gray-900">{title}</h4>
          {subtitle && <p className="text-xs text-gray-500 uppercase tracking-wide">{subtitle}</p>}
        </div>
      </div>
      <p className="text-gray-700 leading-relaxed text-[15px]">{content}</p>
    </div>
  </div>
);

export default SmrPreviewModal;

